import { isSameDay } from 'date-fns';
import { ProductProps } from '@/types';
import { getOnStorage } from '@/shared';
import * as ordersStore from '@/shared/ordersStore';
import { listPayloadsForOfflineView, listPayloadsForSynchedView } from '@/shared/ordersStore';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetchAxios, useNetworkStatusContext, useTables, useUserContext } from '@/hooks';
import { readProductsByCodes } from '@/shared/bfSeller';

export interface OrderProps {
  clientName: string;
  clientRefNumber: string;
  clientCnpj: string;
  orders: OrderItemsProps;
}
export interface OrderItemsProps {
  orderNumber: string;
  erpNumberClient?: string;
  erpNumber: string;
  order_number_origem?: string;
  cart_opened_at?: string;
  origin: string;
  orderNumberMagento: string;
  quantity: number | string;
  status: string;
  isLocal?: boolean;
  reason: string;
  orderDate: string;
  total: string;
  vlr_comissao?: number;
  paymentTerms: string;
  codLoja?: number;
  codSegmentoLoja?: number;
  actionsPending: ItemsProps[];
}
export interface ItemsProps extends ProductProps {
  cod_segmento: number;
  sku: string;
  cod_produto: number;
  product: string;
  price: string;
  unitPrice: string;
  unitPricePerItem: string;
  stPrice: string;
  stPricePackaging: number;
  integrationPrice: number;
  integrationPriceFlex: number;
  quantity: number;
  quantityMultiplo: number;
  percentualFlex?: string;
  packaging: string;
  status: string;
  reason: string;
  promotion_type: string;
  unilever?: boolean;
  price_flex?: number;
  st_price_flex?: number;
  unit_price_flex?: number;
  unit_price_per_item_flex?: number;
}

export const useOrderStatusPage = () => {
  const { tables } = useTables();
  const isLoadedSynchedOrders = useRef(false);
  const { axiosCtx: axiosInstance } = useFetchAxios();
  const { customer, sendingOrders } = useUserContext();
  const { connectivityStatus } = useNetworkStatusContext();

  const isLoading = useRef(false);
  const [data, setData] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [filterDate, setFilterDate] = useState<Date>();
  const [error, setError] = useState<Error | null>(null);
  const [orders, setOrders] = useState<OrderProps[]>([]);
  const [offlineOrders, setOfflineOrders] = useState<OrderProps[]>([]);
  const [products, setProducts] = useState<Record<number, ProductProps>>({});
  const [synchedOrdersList, setSynchedOrdersList] = useState<OrderProps[]>([]);

  /**
   * Sinaliza que a carga dos produtos já aconteceu — inclusive quando ela não trouxe nada. Usar
   * "o dicionário está vazio" como gate deixaria a tela sem montar pedido nenhum quando os
   * produtos citados não existem mais no catálogo.
   */
  const [productsLoaded, setProductsLoaded] = useState(false);

  useEffect(() => {
    if (data?.length && !isLoadedSynchedOrders.current) {
      isLoadedSynchedOrders.current = true;
      
      const originIds = data
        .map((item: any) => item?.orders?.order_number_origem || item?.orders?.order_number_origin || item?.orders?.order_number)
        .filter((id: unknown): id is string => typeof id === 'string' && id.length > 0);

      (async () => {
        for (const id of originIds) {
          try {
            await ordersStore.markConfirmed(id);
          } catch {}
        }
      })();
    }
  }, [data]);

  useEffect(() => {
    // BUG-001: don't hammer the network when offline — without this guard,
    // every render while offline re-fired the fetch and kept the page in a
    // perceived "trying online" state, hiding the offline projection.
    if (loading || data || productsLoaded || isLoading.current || connectivityStatus !== 'online') return;

    isLoading.current = true;

    const rca = getOnStorage('selected_rca');

    const getCodRcasFromClients = (clients: any[] = [], selectedRca?: any) => {
      const codRcas = clients.flatMap((client) => client.lojas?.map((loja: any) => loja.COD_RCA_CLIENTE) || []);

      if (selectedRca?.codPessoa) {
        codRcas.push(selectedRca.codPessoa);
      }

      return Array.from(new Set(codRcas.map(Number).filter(Number.isFinite)));
    };

    const fetchData = async () => {
      try {
        setLoading(true);

        const clients = tables.current.cliente || [];

        const codRcas = getCodRcasFromClients(clients, rca);

        const response = await axiosInstance.get(
          `${window.agp_config.REACT_APP_AWS}/pedido-notifica-get?seqpessoa=${codRcas.join(',')}`
        );

        const mergedPedidos = response.data?.pedidos || [];

        /**
         * Só os produtos citados nos pedidos. O dicionário inteiro obrigava a montar o catálogo
         * todo com preço junto — para uma tela que enriquece algumas dezenas de itens.
         */
        const skus = Array.from(
          new Set(
            mergedPedidos
              .flatMap((pedido: any) => (pedido?.orders?.actions_pending || []).map((item: any) => Number(item?.sku)))
              .filter((sku: number) => Number.isFinite(sku))
          )
        ) as number[];

        const produtosCitados = await readProductsByCodes(skus);
        setProducts(
          produtosCitados.reduce(
            (acc, produto) => {
              acc[Number(produto.COD_PRODUTO)] = produto;
              return acc;
            },
            {} as Record<number, ProductProps>
          )
        );
        setProductsLoaded(true);
        setData(mergedPedidos);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
        isLoading.current = false;
      }
    };

    fetchData();
  }, [axiosInstance, customer.codPessoa, data, loading, productsLoaded, connectivityStatus]);

  const mountOrders = useCallback(
    (orders: any, isLocal?: boolean) => {
      return orders?.map((order: any) => ({
        clientName: order?.company?.name,
        clientRefNumber: order?.company?.id,
        clientCnpj: order?.company?.cnpj,
        cart_opened_at: order?.cart_opened_at,
        orders: {
          isLocal,
          orderNumber: order?.orders?.order_number,
          erpNumber: order?.orders?.erp_order_number,
          orderNumberMagento: order?.orders?.order_number_magento,
          quantity: order?.orders?.items,
          status: order?.orders?.status || 'integrado',
          reason: order?.orders?.reason,
          orderDate: order?.orders?.order_date,
          total: order?.orders?.total,
          vlr_comissao: order?.orders?.vlr_comissao,
          paymentTerms: order?.orders?.payment_terms,
          codLoja: Number(order?.orders?.cod_loja) || undefined,
          codSegmentoLoja: Number(order?.orders?.cod_segmento_loja) || undefined,
          actionsPending: order?.orders?.actions_pending?.map((item: any) => ({
            ...item,
            cod_segmento: item?.cod_segmento,
            sku: item?.sku,
            product: item?.product,
            price: item?.price,
            unitPrice: item?.unit_price,
            unitPricePerItem: item?.unit_price_per_item,
            stPrice: item?.st_price,
            stPricePackaging: item?.st_price_packaging,
            integrationPrice: item?.integration_price,
            integrationPriceFlex: item?.integration_price_flex,
            quantity: item?.quantity,
            quantityMultiplo: item?.quantity_multiplo,
            packaging: item?.packaging,
            percentualFlex: item?.percentual_flex,
            status: '',
            reason: item?.reason,

            unit_price_flex: item?.unit_price_flex,
            unit_price_per_item_flex: item?.unit_price_per_item_flex,
            st_price_flex: item?.st_price_flex,
            price_flex: item?.price_flex,
            unilever: item?.unilever ?? item?.UNILEVER,

            ...products[item?.sku],
          })),
        },
      }));
    },
    [products]
  );

  useEffect(() => {
    if (sendingOrders) return;
    if (offlineOrders?.length && synchedOrdersList?.length) return;

    let cancelled = false;

    const loadLocalOrders = async () => {
      try {
        const [offlinePayloads, synchedPayloads] = await Promise.all([
          listPayloadsForOfflineView(),
          listPayloadsForSynchedView(),
        ]);
        if (cancelled) return;

        if (!offlineOrders?.length && offlinePayloads.length) {
          setOfflineOrders(mountOrders(offlinePayloads, true));
        }

        if (!synchedOrdersList?.length && synchedPayloads.length) {
          const filteredSynchedOrders = synchedPayloads.filter((order) => order?.company?.Cod_rca == customer?.codPessoa);
          setSynchedOrdersList(mountOrders(filteredSynchedOrders, true));
        }
      } catch {
        // IndexedDB indisponível — lista local permanece vazia
      }
    };

    void loadLocalOrders();

    return () => {
      cancelled = true;
    };
  }, [sendingOrders, customer?.codPessoa, mountOrders, offlineOrders?.length, synchedOrdersList?.length]);

  useEffect(() => {
    if (!productsLoaded || connectivityStatus === 'offline') return;

    const orders = mountOrders(data);
    setOrders(orders);
  }, [mountOrders, data, productsLoaded, connectivityStatus]);

  const ordersDate = useMemo(() => {
    return orders?.map((order: any) => {
      const dateString = order?.orders?.orderDate?.replace(/^[^,]*,\s*/, '');
      return new Date(`${dateString.split('/').reverse().join('-')}T00:00:00`);
    });
  }, [orders]);

  const filteredOrders = useMemo(() => {
    if (!orders?.length) return [];

    if (!filterDate) return orders;

    return orders.filter((order) => {
      const orderDate = new Date(
        `${order?.orders?.orderDate
          ?.replace(/^[^,]*,\s*/, '')
          .split('/')
          .reverse()
          .join('-')}T00:00:00`
      );
      return isSameDay(orderDate, filterDate);
    });
  }, [orders, filterDate]);

  const filteredSynchedOrdersList = useMemo(() => {
    if (!synchedOrdersList?.length) return [];

    return synchedOrdersList?.filter(
      (syn) => !filteredOrders.find((filOrd) => filOrd.orders.orderNumber.includes(syn.orders.orderNumber))
    );
  }, [synchedOrdersList, filteredOrders]);

  return {
    error,
    loading,
    filterDate,
    ordersDate,
    ordersList: filteredOrders,
    synchedOrdersList: filteredSynchedOrdersList,
    offlineOrdersList: offlineOrders?.length ? offlineOrders : [],
    setFilterDate,
  };
};
